import * as React from 'react'
import * as Diff from 'diff'

import type { BlockChange, BlockType } from '@basehub/data/replicache/index'

import { BlockIcon } from '~/components/block-icon'
import { DiffLine } from './line'
import { Button } from '~/sotano-kit/primitives/button'
import { useTabsStore } from '../../../(editor)/explore/components/tabs/store'
import { useRepoStore } from '../../../(editor)/explore/components/repo-store'
import { clsx } from 'clsx'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'

export const DiffBlockTitle: React.FC<
  {
    blockChange: BlockChange
    condition: 'added' | 'removed'
    children?: React.ReactNode
    type?: BlockType
  } & (
    | {
        link: false
      }
    | {
        link: true
        parentSourceBlockId: string
      }
  )
> = ({ blockChange, condition, children, type, ...props }) => {
  let title: Diff.Change[] | string = blockChange.title || 'Untitled'
  type =
    type ||
    // consider type conversions
    (condition === 'removed' && blockChange.previous?.type) ||
    blockChange.type

  const addTab = useTabsStore((s) => s.addTab)
  const setFocus = useRepoStore((s) => s.setFocus)

  if (blockChange.current && blockChange.previous) {
    title = Diff.diffChars(
      blockChange.conflicting?.title ?? blockChange.previous.title,
      blockChange.current.title
    ).map((item) => ({ ...item, value: item.value || 'Untitled' }))
  }

  const conditionIsAddedAndBlockWasDeleted =
    condition === 'added' && blockChange.status === 'deleted'

  return (
    <div className="h-9 flex items-center text-dark-muted gap-2 max-w-full">
      {props.link && !conditionIsAddedAndBlockWasDeleted ? (
        <Button
          className="text-dark-muted group diff-title hover:text-dark-dim cursor-default max-w-full"
          unstyled
          focusStyled
          onClick={() => {
            if (!blockChange?.current?.id) return
            addTab({
              key: props.parentSourceBlockId,
              partiallyOpen: true,
            })
            setFocus({
              type: 'block:wrapper',
              blockId: idToSourceBlockId(blockChange.current),
            })
          }}
        >
          <Title
            type={type}
            condition={condition}
            title={title}
            className={clsx(conditionIsAddedAndBlockWasDeleted && 'opacity-30')}
          />
        </Button>
      ) : (
        <Title
          type={type}
          condition={condition}
          title={title}
          className={clsx(
            'diff-title',
            conditionIsAddedAndBlockWasDeleted && 'opacity-30'
          )}
        />
      )}
      {children}
    </div>
  )
}

const Title: React.FC<{
  type: BlockType
  title: string | Diff.Change[]
  condition: 'added' | 'removed'
  className?: string
}> = ({ type, title, condition, className }) => (
  <span
    className={clsx(
      'text-dark-muted hover:text-inherit flex gap-2 text-[13px] items-center font-medium truncate',
      className
    )}
  >
    <BlockIcon
      iconProps={{
        className: 'shrink-0 group-hover:text-inherit',
      }}
      block={{ type }}
    />
    <DiffLine
      diff={title}
      condition={condition}
      className="whitespace-nowrap truncate pr-0.5"
    />
  </span>
)
